import { AxiosInstance, AxiosResponse } from 'axios';
import {
  Company,
  CreateCompanyRequest,
  CreateUserRequest,
  EmailChangeRequest,
  PaginatedResponse,
  SearchParams,
  UpdateCompanyRequest,
  UpdateUserRequest,
  User,
  SubscriptionStatusDto,
  UpdateSubscriptionRequest,
  PriceEntryMode,
  CompanyNote,
  CompanyExportPreferences,
  CompanyExportDownload,
  ExportPreview,
  ReferralAcquisitionResponse,
  ReferralRewardResponse,
  ReverseReferralRewardRequest,
  CompanyAdminSummary,
  AdminOverview,
  AdminCompanyListPage,
} from '../../types';

/** Company and user administration API calls. */
export class AdminApi {
  constructor(private readonly http: AxiosInstance) {}

  async getCompanies(): Promise<Company[]> {
    const response: AxiosResponse<Company[]> = await this.http.get('/companies');
    return response.data;
  }

  async getCompaniesPaginated(params: SearchParams): Promise<PaginatedResponse<Company>> {
    const response: AxiosResponse<PaginatedResponse<Company>> = await this.http.get('/companies/summary/paginated', { params });
    return response.data;
  }

  async getAdminCompaniesPaginated(params: SearchParams): Promise<AdminCompanyListPage> {
    // The shared list state calls this field `name`, while the admin endpoint
    // exposes the same filter as `search`. Keep both callers compatible.
    const { name, ...rest } = params;
    const requestParams = name && !(rest as { search?: string }).search
      ? { ...rest, search: name }
      : rest;
    const response: AxiosResponse<AdminCompanyListPage> = await this.http.get('/companies/admin/paginated', { params: requestParams });
    return response.data;
  }

  async getCompany(id: string): Promise<Company> {
    const response: AxiosResponse<Company> = await this.http.get(`/companies/${id}`);
    return response.data;
  }

  async getCompanyAdminSummary(id: string, startDate: string, endDate: string): Promise<CompanyAdminSummary> {
    const response: AxiosResponse<CompanyAdminSummary> = await this.http.get(`/companies/${id}/admin-summary`, {
      params: { startDate, endDate },
    });
    return response.data;
  }

  async getAdminOverview(startDate: string, endDate: string): Promise<AdminOverview> {
    const response: AxiosResponse<AdminOverview> = await this.http.get('/admin/overview', {
      params: { startDate, endDate },
    });
    return response.data;
  }

  async getCompanyAcquisition(id: string): Promise<ReferralAcquisitionResponse> {
    const response: AxiosResponse<ReferralAcquisitionResponse> = await this.http.get(`/referrals/companies/${id}/acquisition`);
    return response.data;
  }

  async getCompanyReferralRewards(id: string): Promise<ReferralRewardResponse[]> {
    const response: AxiosResponse<ReferralRewardResponse[]> = await this.http.get(`/referrals/companies/${id}/rewards`);
    return response.data;
  }

  async reverseReferralReward(id: string, request: ReverseReferralRewardRequest): Promise<ReferralRewardResponse> {
    const response: AxiosResponse<ReferralRewardResponse> = await this.http.post(`/referrals/rewards/${id}/reverse`, request);
    return response.data;
  }

  async setReferralCodeActive(codeId: string, active: boolean): Promise<void> {
    await this.http.patch(`/referrals/codes/${codeId}`, { active });
  }

  async createCompany(company: CreateCompanyRequest): Promise<Company> {
    const response: AxiosResponse<Company> = await this.http.post('/companies', company);
    return response.data;
  }

  async updateCompany(id: string, company: UpdateCompanyRequest): Promise<Company> {
    const response: AxiosResponse<Company> = await this.http.put(`/companies/${id}`, company);
    return response.data;
  }

  async getCurrentCompany(): Promise<Company> {
    const response: AxiosResponse<Company> = await this.http.get('/companies/current');
    return response.data;
  }

  async updateCurrentPriceEntryMode(priceEntryMode: PriceEntryMode): Promise<Company> {
    const response: AxiosResponse<Company> = await this.http.put('/companies/current/price-entry-mode', { priceEntryMode });
    return response.data;
  }

  async updateCurrentPricingSettings(priceEntryMode: PriceEntryMode, defaultVatRate: number): Promise<Company> {
    const response: AxiosResponse<Company> = await this.http.put('/companies/current/pricing-settings', {
      priceEntryMode,
      defaultVatRate,
    });
    return response.data;
  }

  async updateCurrentBatchTracking(batchTrackingEnabled: boolean): Promise<Company> {
    const response: AxiosResponse<Company> = await this.http.put('/companies/current/batch-tracking', {
      batchTrackingEnabled,
    });
    return response.data;
  }

  async deleteCompany(id: string): Promise<void> {
    await this.http.delete(`/companies/${id}`);
  }

  async getCompanySubscription(id: string): Promise<SubscriptionStatusDto> {
    const response: AxiosResponse<SubscriptionStatusDto> = await this.http.get(`/companies/${id}/subscription`);
    return response.data;
  }

  async updateCompanySubscription(id: string, request: UpdateSubscriptionRequest): Promise<SubscriptionStatusDto> {
    const response: AxiosResponse<SubscriptionStatusDto> = await this.http.put(`/companies/${id}/subscription`, request);
    return response.data;
  }

  async getCurrentSubscription(): Promise<SubscriptionStatusDto> {
    const response: AxiosResponse<SubscriptionStatusDto> = await this.http.get('/subscription');
    return response.data;
  }

  async getCompanyNotes(companyId: string): Promise<CompanyNote[]> {
    const response: AxiosResponse<CompanyNote[]> = await this.http.get(`/companies/${companyId}/notes`);
    return response.data;
  }

  async addCompanyNote(companyId: string, note: string): Promise<CompanyNote> {
    const response: AxiosResponse<CompanyNote> = await this.http.post(`/companies/${companyId}/notes`, { note });
    return response.data;
  }

  async getExportPreferences(): Promise<CompanyExportPreferences> {
    const response: AxiosResponse<CompanyExportPreferences> = await this.http.get('/exports/preferences');
    return response.data;
  }

  async updateExportPreferences(preferences: Omit<CompanyExportPreferences, 'maxRows'>): Promise<CompanyExportPreferences> {
    const response: AxiosResponse<CompanyExportPreferences> = await this.http.put('/exports/preferences', preferences);
    return response.data;
  }

  async getExportPreview(): Promise<ExportPreview> {
    const response: AxiosResponse<ExportPreview> = await this.http.get('/exports/preview');
    return response.data;
  }

  async exportCompanyData(): Promise<CompanyExportDownload> {
    const response: AxiosResponse<Blob> = await this.http.get('/exports', { responseType: 'blob' });
    const contentDisposition = response.headers['content-disposition'] || '';
    const encodedFileName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const quotedFileName = contentDisposition.match(/filename="([^"]+)"/i)?.[1];
    const fileName = encodedFileName
      ? decodeURIComponent(encodedFileName)
      : quotedFileName || 'Yazbiveresiye.xlsx';
    return { blob: response.data, fileName };
  }

  async searchCompanies(params: SearchParams): Promise<Company[]> {
    const response: AxiosResponse<Company[]> = await this.http.get('/companies/search', { params });
    return response.data;
  }

  async createCompanyUser(companyId: string, user: CreateUserRequest): Promise<User> {
    const response: AxiosResponse<User> = await this.http.post(`/users/company/${companyId}`, user);
    return response.data;
  }

  async getUsers(): Promise<User[]> {
    const response: AxiosResponse<User[]> = await this.http.get('/users');
    return response.data;
  }

  async getUsersByCompany(companyId: string): Promise<User[]> {
    const response: AxiosResponse<User[]> = await this.http.get(`/users/company/${companyId}`);
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    const response: AxiosResponse<User> = await this.http.get(`/users/${id}`);
    return response.data;
  }

  async createEmployee(user: CreateUserRequest): Promise<User> {
    const response: AxiosResponse<User> = await this.http.post('/users/employees', user);
    return response.data;
  }

  async updateUser(id: string, user: UpdateUserRequest): Promise<User> {
    const response: AxiosResponse<User> = await this.http.put(`/users/${id}`, user);
    return response.data;
  }

  async requestEmailChange(id: string, request: EmailChangeRequest): Promise<User> {
    const response: AxiosResponse<User> = await this.http.post(`/users/${id}/email-change`, request);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.http.delete(`/users/${id}`);
  }
}
